import React, { useEffect, useMemo, useRef, useState } from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const toISO = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};
const parseISO = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    const date = new Date(Date.UTC(y, m - 1, d));
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};
const addMonths = (date, delta) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + delta);
    return d;
};
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isBefore = (a, b) => a && b && a.setHours(0, 0, 0, 0) < b.setHours(0, 0, 0, 0);
const isAfter = (a, b) => a && b && a.setHours(0, 0, 0, 0) > b.setHours(0, 0, 0, 0);
const clamp = (date, min, max) => {
    let d = date;
    if (min && isBefore(d, min)) d = min;
    if (max && isAfter(d, max)) d = max;
    return d;
};

const WEEKDAYS_SUN_FIRST = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const WEEKDAYS_MON_FIRST = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function getMonthMatrix(viewDate, weekStartsOnMonday = false) {
    const firstOfMonth = startOfMonth(viewDate);
    const startWeekday = firstOfMonth.getDay();
    const offset = weekStartsOnMonday ? (startWeekday === 0 ? 6 : startWeekday - 1) : startWeekday;
    const start = new Date(firstOfMonth);
    start.setDate(firstOfMonth.getDate() - offset);

    const weeks = [];
    for (let w = 0; w < 6; w++) {
        const row = [];
        for (let d = 0; d < 7; d++) {
            const cell = new Date(start);
            cell.setDate(start.getDate() + w * 7 + d);
            row.push(cell);
        }
        weeks.push(row);
    }
    return weeks;
}

function CalendarIcon({ className = 'h-5 w-5' }) {
    return (
        <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 2v4M16 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

export default function DatePicker({
    label,
    helperText,
    error,
    required = false,

    name,
    id,
    placeholder = 'YYYY-MM-DD',

    value,
    defaultValue,
    onChange,
    onValueChange,

    minDate,
    maxDate,
    isDateDisabled,

    closeOnSelect = true,
    weekStartsOn = 'sun',

    variant = 'outline',
    size = 'md',
    fullWidth = true,

    leftIcon,
    rightIcon,
    clearable = false,
    onClear,

    disabled = false,
    readOnly = false,
    loading = false,

    className = '',
    inputClassName = '',
    containerClassName = '',
    style,
}) {
    const uncontrolled = value === undefined;
    const [internal, setInternal] = useState(defaultValue || '');
    const v = uncontrolled ? internal : value || '';
    const selectedDate = useMemo(() => parseISO(v), [v]);

    const minD = useMemo(() => parseISO(minDate), [minDate]);
    const maxD = useMemo(() => parseISO(maxDate), [maxDate]);

    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => selectedDate || new Date());
    const containerRef = useRef(null);
    const gridRef = useRef(null);

    useEffect(() => {
        if (selectedDate) setViewDate(selectedDate);
    }, [v]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target)) setOpen(false);
        };
        const onEsc = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open]);

    const setValue = (next, event) => {
        if (uncontrolled) setInternal(next);
        onValueChange?.(next);
        onChange?.(event ?? null, next);
    };

    const isOutOfRange = (date) => (minD && isBefore(date, minD)) || (maxD && isAfter(date, maxD));
    const isDisabledDate = (date) => !!(isDateDisabled && isDateDisabled(date));

    const weekMonday = weekStartsOn === 'mon';
    const weeks = getMonthMatrix(viewDate, weekMonday);
    const weekdays = weekMonday ? WEEKDAYS_MON_FIRST : WEEKDAYS_SUN_FIRST;

    const sizeMap = {
        sm: 'h-8 text-sm px-2',
        md: 'h-10 text-sm px-3',
        lg: 'h-12 text-base px-3.5',
    };

    const variantMap = {
        outline: 'bg-white border border-gray-300 focus-within:ring-2 focus-within:ring-blue-600 rounded-md',
        filled: 'bg-gray-50 border border-transparent focus-within:ring-2 focus-within:ring-blue-600 rounded-md',
        unstyled: '',
    };

    const inputPadLeft = leftIcon ? 'pl-9' : 'pl-3';
    const inputPadRight = 'pr-10';

    const onDaySelect = (date) => {
        if (isOutOfRange(date) || isDisabledDate(date)) return;
        const next = toISO(date);
        setValue(next);
        if (closeOnSelect) setOpen(false);
    };

    const gotoPrevMonth = () => setViewDate((d) => addMonths(d, -1));
    const gotoNextMonth = () => setViewDate((d) => addMonths(d, +1));
    const gotoToday = () => setViewDate(new Date());

    const onGridKeyDown = (e) => {
        if (!open) return;
        const focusable = gridRef.current?.querySelector('[data-focus="true"]');
        const base = focusable ? new Date(Number(focusable.dataset.time)) : selectedDate || new Date(viewDate);
        let next = new Date(base);
        if (e.key === 'ArrowLeft') { next.setDate(base.getDate() - 1); e.preventDefault(); }
        else if (e.key === 'ArrowRight') { next.setDate(base.getDate() + 1); e.preventDefault(); }
        else if (e.key === 'ArrowUp') { next.setDate(base.getDate() - 7); e.preventDefault(); }
        else if (e.key === 'ArrowDown') { next.setDate(base.getDate() + 7); e.preventDefault(); }
        else if (e.key === 'PageUp') { next = addMonths(base, -1); e.preventDefault(); }
        else if (e.key === 'PageDown') { next = addMonths(base, +1); e.preventDefault(); }
        else if (e.key === 'Home') { next.setDate(base.getDate() - (base.getDay() || 7) + 1); e.preventDefault(); }
        else if (e.key === 'End') { next.setDate(base.getDate() + (7 - (base.getDay() || 7))); e.preventDefault(); }
        else if (e.key === 'Enter' || e.key === ' ') {
            onDaySelect(base);
            e.preventDefault();
            return;
        } else {
            return;
        }
        setViewDate(next);
    };

    const clear = () => {
        onClear?.();
        setValue('');
    };

    const helpOrError = error || helperText;

    return (
        <div
            ref={containerRef}
            className={cx(fullWidth && 'w-full', containerClassName, className)}
            style={style}
        >
            {label && (
                <label
                    htmlFor={id || name}
                    className={cx('mb-1 block text-sm font-medium', error ? 'text-red-700' : 'text-gray-700')}
                >
                    {label}
                    {required && <span className="ml-0.5 text-red-600">*</span>}
                </label>
            )}

            <div className={cx('relative', variantMap[variant])}>
                {leftIcon && (
                    <span className="pointer-events-none absolute inset-y-0 left-2 inline-flex items-center text-gray-400">
                        {leftIcon}
                    </span>
                )}

                <input
                    id={id || name}
                    name={name}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={disabled}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    value={v}
                    onChange={(e) => {
                        const next = e.target.value;
                        setValue(next, e);
                    }}
                    onFocus={() => !readOnly && !disabled && setOpen(true)}
                    className={cx(
                        'block w-full bg-transparent outline-none',
                        'placeholder:text-gray-400',
                        sizeMap[size],
                        inputPadLeft,
                        inputPadRight,
                        error ? 'text-red-900' : 'text-gray-900',
                        inputClassName
                    )}
                />

                <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                    {clearable && v && !disabled && !readOnly && (
                        <button
                            type="button"
                            onClick={clear}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                            aria-label="Clear date"
                            title="Clear"
                        >
                            <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
                                <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => (!disabled && !readOnly) && setOpen((o) => !o)}
                        className={cx(
                            'rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600',
                            disabled && 'pointer-events-none opacity-50'
                        )}
                        aria-label={open ? 'Close calendar' : 'Open calendar'}
                    >
                        {rightIcon || <CalendarIcon />}
                    </button>
                </div>

                {open && !disabled && (
                    <div
                        role="dialog"
                        aria-modal="false"
                        className="absolute z-50 mt-1 w-auto min-w-[18rem] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
                    >
                        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={gotoPrevMonth}
                                    className="rounded p-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                    aria-label="Previous month"
                                >
                                    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
                                        <path d="M12.5 15L7.5 10l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={gotoToday}
                                    className="rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                    aria-label="Go to today"
                                >
                                    Today
                                </button>
                                <button
                                    type="button"
                                    onClick={gotoNextMonth}
                                    className="rounded p-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                                    aria-label="Next month"
                                >
                                    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
                                        <path d="M7.5 5l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                            <div className="text-sm font-medium text-gray-800">
                                {viewDate.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
                            </div>
                            <div className="w-12" />
                        </div>

                        <div className="grid grid-cols-7 text-center text-[11px] uppercase tracking-wide text-gray-500">
                            {weekdays.map((w) => (
                                <div key={w} className="px-1 py-1">{w}</div>
                            ))}
                        </div>

                        <div
                            ref={gridRef}
                            role="grid"
                            tabIndex={0}
                            onKeyDown={onGridKeyDown}
                            className="grid grid-cols-7 p-1"
                        >
                            {weeks.map((row, rIdx) => row.map((d, cIdx) => {
                                const inMonth = d.getMonth() === viewDate.getMonth();
                                const today = isSameDay(d, new Date());
                                const selected = selectedDate && isSameDay(d, selectedDate);
                                const outOfRange = isOutOfRange(d);
                                const disabledDay = isDisabledDate(d);
                                const disabledCell = outOfRange || disabledDay;

                                const base = 'm-0.5 rounded-md text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600';
                                const pad = 'px-2 py-1.5';
                                const color = selected
                                    ? 'bg-blue-600 text-white'
                                    : today
                                        ? 'bg-blue-50 text-blue-700'
                                        : inMonth
                                            ? 'text-gray-900 hover:bg-gray-100'
                                            : 'text-gray-400 hover:bg-gray-100';
                                const disabledCls = disabledCell ? 'pointer-events-none opacity-40' : 'cursor-pointer';

                                const time = d.getTime();
                                const focusAttr = selected ? { 'data-focus': 'true' } : {};

                                return (
                                    <button
                                        key={`${rIdx}-${cIdx}`}
                                        type="button"
                                        role="gridcell"
                                        aria-selected={selected}
                                        aria-disabled={disabledCell}
                                        className={cx(base, pad, color, disabledCls)}
                                        onClick={() => onDaySelect(d)}
                                        {...focusAttr}
                                        data-time={time}
                                    >
                                        {d.getDate()}
                                    </button>
                                );
                            }))}
                        </div>
                    </div>
                )}
            </div>

            {helpOrError && (
                <p className={cx('mt-1 text-xs', error ? 'text-red-600' : 'text-gray-500')}>
                    {helpOrError}
                </p>
            )}
        </div>
    );
}
